import React, { useState, useRef, useEffect } from "react";
import "./login.css";
import { _post } from "../services/apiClient";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { getAppConfig } from "../utilis/appConfig";
import { consumePostLoginRedirect } from "../utilis/customerSession";
import { useRestaurantDetails } from "../context/RestaurantDetailsContext";
import ServiceUnavailable from "./ServiceUnavailable";

export default function LoginSignup() {
  const [isSignup, setIsSignup] = useState(false);
  const [mobile, setMobile] = useState("");
  const [signup, setSignup] = useState({ name: "", mobile: "", email: "" });
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isOtpScreen, setIsOtpScreen] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const [timer, setTimer] = useState(0);
  const [pendingSignup, setPendingSignup] = useState(null);
  const { t, i18n } = useTranslation();
  const config = getAppConfig();
  const { restaurantDetails } = useRestaurantDetails();
  // Admin → Configure → "OTP Channel" (whatsapp / email / both)
  const otpChannels = config.otpChannels?.length ? config.otpChannels : ["whatsapp"];
  const [otpChannel, setOtpChannel] = useState(otpChannels[0]);
  const emailRequired = otpChannel === "email";
  const otpSentText = (res) =>
    otpChannel === "email" ? res.data.message : t("otpSent");

  // Auto-clear messages after 3 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  // Countdown timer for Resend OTP
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleClose = () => {
    window.location = consumePostLoginRedirect();
  };

  // "Send code via WhatsApp / Email" — only when Admin allows both
  const renderChannelPicker = (emailHint) =>
    otpChannels.length > 1 && (
      <div className="text-start">
        <span className="block text-sm font-medium text-gray-700 mb-2">
          {t("sendCodeVia", "Send code via")}
        </span>
        <div className="grid grid-cols-2 gap-2">
          {otpChannels.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setOtpChannel(c)}
              className={`py-2 rounded-lg border text-sm font-medium transition-colors ${
                otpChannel === c
                  ? "border-brand-500 bg-brand-50 text-brand-700"
                  : "border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {c === "email" ? t("email", "Email") : "WhatsApp"}
            </button>
          ))}
        </div>
        {otpChannel === "email" && (
          <p className="text-xs text-gray-500 mt-1.5">{emailHint}</p>
        )}
      </div>
    );

  const mobileRegex = /^[0-9]{10}$/;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleSignup = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    setLoading(true);

    if (!mobileRegex.test(signup.mobile)) {
      setMessage({
        type: "error",
        text: t("invalidMobile"),
      });
      setLoading(false);
      return;
    }
    if (emailRequired && !signup.email) {
      setMessage({
        type: "error",
        text: t("emailRequiredForCode", "Please enter your email to receive the code"),
      });
      setLoading(false);
      return;
    }
    if (signup.email && !emailRegex.test(signup.email)) {
      setMessage({
        type: "error", text: t("invalidEmail"),
      });
      setLoading(false);
      return;
    }

    try {
      const res = await _post("/auth/userSignupOtp", {
        name: signup.name,
        phoneNumber: signup.mobile,
        email: signup.email,
        channel: otpChannel,
      });

      if (res.data.success) {
        setMessage({ type: "success", text: otpSentText(res) });
        setIsOtpScreen(true);
        setTimer(120);
        setPendingSignup({ ...signup });
        setMobile(signup.mobile);
      } else {
        setMessage({
          type: "error",
          text: res.data.message || t("signupFailed"),
        });
      }
    } catch (err) {
      setMessage({
        type: "error",
        text:
          err.response?.data?.message || "Something went wrong during signup",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    setLoading(true);

    if (!mobileRegex.test(mobile)) {
      setMessage({
        type: "error",
        text: t("invalidMobile"),
      });
      setLoading(false);
      return;
    }

    try {
      const res = await _post("/auth/userLogin", { phoneNumber: mobile, channel: otpChannel });

      if (res.data.success) {
        setMessage({ type: "success", text: otpSentText(res) });
        setIsOtpScreen(true);
        setTimer(120);
      } else {
        setMessage({ type: "error", text: res.data.message || t("loginFailed") });
      }
    } catch (err) {
      setMessage({
        type: "error",
        text:
          err.response?.data?.message || "Something went wrong during login",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      if (isSignup && pendingSignup) {
        const res = await _post("/auth/userSignupOtp", {
          name: pendingSignup.name,
          phoneNumber: pendingSignup.mobile,
          email: pendingSignup.email,
          channel: otpChannel,
        });
        if (res.data.success) {
          setMessage({ type: "success", text: t("otpResent") });
          setTimer(120);
        } else {
          setMessage({
            type: "error",
            text: res.data.message || t("resendFailed"),
          });
        }
      } else {
        const res = await _post("/auth/userLogin", { phoneNumber: mobile, channel: otpChannel });
        if (res.data.success) {
          setMessage({ type: "success", text: t("otpResent") });
          setTimer(120);
        } else {
          setMessage({
            type: "error",
            text: res.data.message || t("resendFailed"),
          });
        }
      }
    } catch (err) {
      setMessage({
        type: "error",
        // e.g. "Please wait 45 seconds before requesting a new code."
        text:
          err.response?.data?.message ||
          "Something went wrong while resending OTP",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e, index) => {
    const val = e.target.value.replace(/\D/g, "");

    if (val.length > 1) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = val[i] || "";
      }
      setOtp(newOtp);

      const otpValue = newOtp.join("");
      if (otpValue.length === 6 && !loading) {
        handleVerifyOtpAuto(otpValue);
      }
    } else {
      setOtp((prev) => {
        const newOtp = [...prev];
        newOtp[index] = val[0] || "";

        const otpValue = newOtp.join("");
        if (otpValue.length === 6 && !loading) {
          handleVerifyOtpAuto(otpValue);
        }

        return newOtp;
      });

      if (val && index < 5) inputRefs.current[index + 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("Text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newOtp = Array(6).fill("");
    for (let i = 0; i < pasteData.length; i++) newOtp[i] = pasteData[i];
    setOtp(newOtp);

    if (pasteData.length === 6) handleVerifyOtpAuto(pasteData);
  };

  // FIX: Prevent duplicate calls and race conditions
  const handleVerifyOtpAuto = async (otpValue) => {
    if (loading) return; // Prevent duplicate API calls

    setLoading(true);
    try {
      if (isSignup && pendingSignup) {
        const res = await _post("/auth/userSignup", {
          name: pendingSignup.name,
          phoneNumber: pendingSignup.mobile,
          email: pendingSignup.email,
          otp: otpValue,
        });

        if (res.data.success) {
          localStorage.setItem("token", res.data.token);
          const decoded = jwtDecode(res.data.token);
          setMessage({
            type: "success",
            text: t("signupSuccess"),
          });
          // Only redirect after short delay, don't show error
          setTimeout(() => (window.location = consumePostLoginRedirect()), 1200);
        } else {
          // FIX: Clear OTP state if verification fails
          setOtp(Array(6).fill(""));
          setMessage({
            type: "error",
            text: res.data.message || t("otpVerificationFailed"),
          });
          setLoading(false);
        }
      } else {
        const res = await _post("/auth/verifyOtp", {
          phoneNumber: mobile,
          otp: otpValue,
        });

        if (res.data.success) {
          localStorage.setItem("token", res.data.token);
          const decoded = jwtDecode(res.data.token);
          setMessage({
            type: "success",
            text: t("loginSuccess"),
          });
          // Only redirect after short delay, don't show error
          setTimeout(() => (window.location = consumePostLoginRedirect()), 1200);
        } else {
          // FIX: Clear OTP state if verification fails
          setOtp(Array(6).fill(""));
          setMessage({
            type: "error",
            text: res.data.message || t("otpVerificationFailed"),
          });
          setLoading(false);
        }
      }
    } catch (err) {
      // FIX: Clear OTP state on error
      setOtp(Array(6).fill(""));
      setMessage({
        type: "error",
        text:
          err.response?.data?.message ||
          "Something went wrong during OTP verification",
      });
      setLoading(false);
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length < 6) {
      setMessage({ type: "error", text: "Enter complete 6-digit OTP" });
      return;
    }

    setLoading(true);
    try {
      if (isSignup && pendingSignup) {
        const res = await _post("/auth/userSignup", {
          name: pendingSignup.name,
          phoneNumber: pendingSignup.mobile,
          email: pendingSignup.email,
          otp: otpValue,
        });

        if (res.data.success) {
          localStorage.setItem("token", res.data.token);
          const decoded = jwtDecode(res.data.token);
          setMessage({
            type: "success",
            text: "Signup successful. Redirecting...",
          });
          setTimeout(() => (window.location = consumePostLoginRedirect()), 1200);
        } else {
          setOtp(Array(6).fill(""));
          setMessage({
            type: "error",
            text: res.data.message || "OTP verification failed",
          });
        }
      } else {
        const res = await _post("/auth/verifyOtp", {
          phoneNumber: mobile,
          otp: otpValue,
        });

        if (res.data.success) {
          localStorage.setItem("token", res.data.token);
          const decoded = jwtDecode(res.data.token);
          setMessage({
            type: "success",
            text: "Login successful. Redirecting...",
          });
          setTimeout(() => (window.location = consumePostLoginRedirect()), 1200);
        } else {
          setOtp(Array(6).fill(""));
          setMessage({
            type: "error",
            text: res.data.message || "OTP verification failed",
          });
        }
      }
    } catch (err) {
      setOtp(Array(6).fill(""));
      setMessage({
        type: "error",
        text:
          err.response?.data?.message ||
          "Something went wrong during OTP verification",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOtpScreen) inputRefs.current[0].focus();
  }, [isOtpScreen]);

  if (restaurantDetails && restaurantDetails.amcStatus && restaurantDetails.amcStatus !== "active") {
    return <ServiceUnavailable />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-brand-50/30 to-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Back Button */}
        <div className="text-center mb-8">
          <button
            onClick={handleClose}
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-brand-600 transition-colors mb-6"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>{t("backToHome")}</span>
          </button>
          <img
            alt="Logo"
            src={
              config.logo
                ? `${process.env.REACT_APP_IMAGE_BASE_URL}${config.logo}`
                : "/img/finglider_logo.jpg"
            }
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/img/finglider_logo.jpg";
            }}
            className="h-12 mx-auto mb-4"
          />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-brand-100/50">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-700 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">
              {isOtpScreen
                ? t("verifyOtp")
                : isSignup
                  ? t("createAccount")
                  : t("welcomeBack")}
            </h2>

            <p className="text-gray-500 mt-2">
              {isOtpScreen
                ? t("otpInstruction")
                : isSignup
                  ? t("signupToGetStarted")
                  : t("loginToContinue")}
            </p>
          </div>

          {!isOtpScreen ? (
            !isSignup ? (
              <form className="space-y-4" onSubmit={handleLogin}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-start">
                    {t("mobileNumber")}
                  </label>
                  <input
                    type="tel"
                    placeholder={t("enterMobileNumber")}
                    value={mobile}
                    onChange={(e) =>
                      setMobile(e.target.value.replace(/\D/g, ""))
                    }
                    required
                    maxLength="10"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                {renderChannelPicker(
                  t("emailCodeToAccount", "Code goes to the email saved on your account"),
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-brand-500 to-brand-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <span className="inline-flex items-center">
                      <svg
                        className="animate-spin h-5 w-5 mr-2"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      {t("sendingOtp")}
                    </span>
                  ) : (
                    t("sendOtp")
                  )}
                </button>
              </form>
            ) : (
              <form className="space-y-4" onSubmit={handleSignup}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-start">
                    {t("fullName")}
                  </label>
                  <input
                    type="text"
                    placeholder={t("enterFullName")}
                    value={signup.name}
                    onChange={(e) =>
                      setSignup({ ...signup, name: e.target.value })
                    }
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-start">
                    {t("mobileNumber")}
                  </label>
                  <input
                    type="tel"
                    placeholder={t("enterMobileNumber")}
                    value={signup.mobile}
                    onChange={(e) =>
                      setSignup({
                        ...signup,
                        mobile: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    required
                    maxLength="10"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-start">
                    {emailRequired ? t("email", "Email") : t("emailOptional")}
                  </label>
                  <input
                    type="email"
                    placeholder={t("enterEmail")}
                    value={signup.email}
                    onChange={(e) =>
                      setSignup({ ...signup, email: e.target.value })
                    }
                    required={emailRequired}
                    pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                {renderChannelPicker(
                  t("emailCodeToAbove", "Code goes to the email above"),
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-brand-500 to-brand-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <span className="inline-flex items-center">
                      <svg
                        className="animate-spin h-5 w-5 mr-2"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      {t("creatingAccount")}
                    </span>
                  ) : (
                    t("createAccount")
                  )}
                </button>
              </form>
            )
          ) : (
            <form className="space-y-4" onSubmit={handleVerifyOtp}>
              <div className="text-center mb-4">
                <p className="text-sm text-gray-600">
                  {t("otpSentTo", {
                    phone: isSignup && pendingSignup ? pendingSignup.mobile : mobile,
                  })}
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setIsOtpScreen(false);
                    setOtp(Array(6).fill(""));
                  }}
                  className="text-sm text-brand-600 hover:text-brand-700 font-medium mt-1"
                >
                  {t("changeNumber")}
                </button>
              </div>

              {/* FIXED: Added dir="ltr" to keep OTP inputs left-to-right in all languages */}
              <div className="flex gap-2 justify-center mb-4" dir="ltr">
                {otp.map((val, idx) => (
                  <input
                    key={idx}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="1"
                    value={val}
                    onChange={(e) => handleOtpChange(e, idx)}
                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                    onPaste={handleOtpPaste}
                    ref={(el) => (inputRefs.current[idx] = el)}
                    className="w-12 h-12 text-center text-xl font-semibold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all"
                  />
                ))}
              </div>

              <div className="text-center">
                {timer > 0 ? (
                  <p className="text-sm text-gray-600">
                    <Trans
                      i18nKey="resendOtpIn"
                      values={{ seconds: timer }}
                      components={{
                        strong: (
                          <span className="font-semibold text-brand-600" />
                        ),
                      }}
                    />
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    className="text-sm text-brand-600 hover:text-brand-700 font-medium"
                  >
                    {t("resendOtp")}
                  </button>
                )}
              </div>
            </form>
          )}

          {message.text && (
            <div
              className={`mt-4 p-3 rounded-lg text-sm font-medium ${message.type === "error"
                ? "bg-red-50 text-red-600 border border-red-200"
                : "bg-green-50 text-green-600 border border-green-200"
                }`}
            >
              {message.text}
            </div>
          )}

          {!isOtpScreen && (
            <div className="mt-6 text-center text-sm">
              {!isSignup ? (
                <p className="text-gray-600">
                  {t("dontHaveAccount")}{" "}
                  <button
                    onClick={() => setIsSignup(true)}
                    className="text-brand-600 hover:text-brand-700 font-semibold"
                  >
                    {t("signup")}
                  </button>
                </p>
              ) : (
                <p className="text-gray-600">
                  {t("alreadyHaveAccount")}{" "}
                  <button
                    onClick={() => setIsSignup(false)}
                    className="text-brand-600 hover:text-brand-700 font-semibold"
                  >
                    {t("login")}
                  </button>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}